import { SEO } from "@/components/SEO";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "@/lib/router-compat";
import {
  ArrowRight,
  ChevronDown,
  Clock,
  DollarSign,
  FileText,
  Globe,
  Layers,
  LifeBuoy,
  Rocket,
  ShoppingCart,
  Sparkles,
  Users,
} from "lucide-react";

const CANONICAL = "https://cargonova.ai/compare/cargonova-vs-cargowise";
const CANONICAL_ZH = "https://cargonova.ai/zh/compare/cargonova-vs-cargowise";
const CANONICAL_TH = "https://cargonova.ai/th/compare/cargonova-vs-cargowise";
const PUBLISHED = "2026-07-06";
const UPDATED = "2026-07-06";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline:
    "CargoNova vs CargoWise: A Solution Comparison for Modern Freight Forwarders (2026)",
  description:
    "Compare CargoNova and CargoWise across pricing, deployment, user experience, integrations, support and global coverage for modern freight forwarders and cross-border logistics teams.",
  datePublished: PUBLISHED,
  dateModified: UPDATED,
  author: { "@type": "Organization", name: "CargoNova" },
  publisher: { "@id": "https://cargonova.ai/#organization" },
  mainEntityOfPage: CANONICAL,
  inLanguage: "en",
  about: [
    "Freight forwarding software comparison",
    "AI freight forwarding",
    "Cross-border logistics platform",
    "TMS evaluation",
  ],
};

const contentEn = {
  eyebrow: "CargoWise Alternative",
  title: "CargoNova vs CargoWise:\nWhich Fits Your Freight Business?",
  summary:
    "CargoNova helps fast-growing logistics teams run global operations with real-time visibility — built for modern forwarding workflows.",
  intro:
    "CargoWise offers broad functionality, but its heavy architecture, long implementation and desktop-style interface can slow agile teams. CargoNova is a cloud-native, AI-native platform for modern international freight.",

  audienceTitle: "Who is CargoNova for?",
  audience: [
    {
      icon: ShoppingCart,
      title: "Cross-border e-commerce logistics",
      body:
        "For DTC brands, Amazon sellers and cross-border merchants. CargoNova connects natively to e-commerce platforms and overseas WMS for lighter, smarter workflows.",
    },
    {
      icon: Users,
      title: "SME freight forwarders",
      body:
        "For 10–100 person teams growing fast. CargoNova works out of the box, so you focus on growth instead of system maintenance.",
    },
    {
      icon: Globe,
      title: "Global teams needing local support",
      body:
        "For companies expanding globally that need multi-language, multi-currency and responsive local support. CargoNova offers bilingual English / Chinese support and on-site implementation.",
    },
  ],

  tableTitle: "CargoNova vs CargoWise: Capability Comparison",
  tableNote:
    "Comparison based on CargoNova's product design and public CargoWise information as of 2026. Confirm critical details with each vendor.",
  tableHeaders: ["Comparison Dimension", "CargoWise", "CargoNova"],
  table: [
    {
      icon: DollarSign,
      capability: "Pricing",
      subtitle: "Commercial model",
      cargowise: "Mixed transaction-based and per-user licensing, harder for SMEs to forecast.",
      cargonova: "Transparent subscription plus usage-based pricing for predictable monthly costs.",
      badge: "Better fit for SME",
    },
    {
      icon: Rocket,
      capability: "Deployment",
      subtitle: "Time to go-live",
      cargowise: "Often needs consultants; typical go-live is 30–90 days.",
      cargonova: "Cloud-native with standard migration templates; go-live in weeks.",
      badge: "Fast setup",
    },
    {
      icon: Sparkles,
      capability: "User Interface",
      subtitle: "UI / UX experience",
      cargowise: "Feature-rich desktop interface with deep menus; requires dedicated training.",
      cargonova: "Modern web cockpit built around AI copilots; new users productive in days.",
      badge: "Intuitive",
    },
    {
      icon: Layers,
      capability: "Integration",
      subtitle: "Ecosystem connectivity",
      cargowise: "Established integrations, though newer e-commerce or ERP tools may need extra work.",
      cargonova: "Open RESTful APIs with native connectors to e-commerce, WMS, customs and tracking providers.",
      badge: "Connected",
    },
    {
      icon: LifeBuoy,
      capability: "Support",
      subtitle: "Customer support",
      cargowise: "Ticket-based global support; local Asia-Pacific coverage varies.",
      cargonova: "Dedicated 24/7 channels, bilingual English / Chinese support and on-site implementation.",
      badge: "24/7 dedicated",
    },
    {
      icon: Globe,
      capability: "Global Coverage",
      subtitle: "Global reach",
      cargowise: "Very broad — covers customs, compliance and tax logic across most major countries and ports worldwide.",
      cargonova: "Strong coverage of mainstream international lanes and China / EU / US compliance; niche country-specific customs coverage is expanding.",
      badge: "Core covered",
      neutral: true,
    },
  ],

  benefitsTitle: "Why teams switch to CargoNova",
  benefits: [
    {
      metric: "Weeks",
      title: "Faster go-live",
      body:
        "Cloud-native architecture and migration templates get you live in weeks, not quarters.",
    },
    {
      metric: "2×",
      title: "Operational efficiency",
      body:
        "AI agents automate quoting, B/Ls, reconciliation and tracking — so a 10-person team handles what used to need twice as many people.",
    },
    {
      metric: "100%",
      title: "Digital customer experience",
      body:
        "Client portal lets customers track shipments, download invoices and place orders online — improving retention and cutting overhead.",
    },
  ],

  faqTitle: "Frequently Asked Questions",
  faqs: [
    {
      q: "Is CargoNova an alternative to CargoWise?",
      a: "Yes. CargoNova is a next-generation, AI-native freight forwarding platform for modern forwarders, e-commerce logistics providers and mid-market supply chain teams — delivering core international logistics capabilities in a lighter, smarter and more cost-effective package.",
    },
    {
      q: "How difficult is migration to CargoNova?",
      a: "Our implementation team provides end-to-end support with mature data migration tools for customer records, historical invoices and open shipments. Most SME teams complete migration in weeks with business continuity maintained.",
    },
    {
      q: "Is CargoNova more cost-effective than CargoWise?",
      a: "For most SME and mid-market forwarders, yes. CargoNova uses transparent subscription plus usage-based pricing. Customers typically report meaningful IT cost reductions after switching, though exact savings depend on team size, modules and integrations.",
    },
    {
      q: "When would CargoNova not be the right fit?",
      a: "If you are a very large multinational forwarder with tens of thousands of staff and offices in most countries, relying on highly specialized customs systems in remote jurisdictions, CargoWise remains a safe choice. For fast-growing teams focused on China, EU, US or mainstream cross-border e-commerce lanes, CargoNova is a strong efficiency fit.",
    },
  ],

  ctaTitle: "Ready to modernize your logistics?",
  ctaBody:
    "Book a live walkthrough to see AI agents handle quoting, booking, documentation, customs and settlement in a modern web cockpit.",
  ctaButton: "Book a demo",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "Back to FAQ",
  backTo: "/",
  disclaimer:
    "CargoWise® is a registered trademark of WiseTech Global. CargoNova is not affiliated with, endorsed by, or sponsored by WiseTech Global. All product names, logos and brands are the property of their respective owners and are used here solely for identification and comparison. Comparison data reflects CargoNova's product design and publicly available information as of 2026; please confirm evaluation-critical details directly with each vendor.",
};

const contentZh = {
  eyebrow: "深度对比",
  title: "CargoNova 与 CargoWise：\n谁更适合您的货代业务？",
  summary:
    "CargoNova 帮助快速成长的物流团队实现全球运营与实时可视，专为现代货代流程设计。",
  intro:
    "CargoWise 功能全面，但较重的架构、较长的实施周期和桌面式交互会拖慢敏捷团队。CargoNova 是面向现代国际货代的云原生、AI Native 平台。",

  audienceTitle: "CargoNova 适合谁？",
  audience: [
    {
      icon: ShoppingCart,
      title: "跨境电商物流",
      body:
        "服务独立站、亚马逊等跨境卖家，处理一件代发、FBA 头程和专线小包。CargoNova 原生对接主流电商平台与海外仓 WMS，流程更轻、操作更智能。",
    },
    {
      icon: Users,
      title: "中小型货代企业",
      body:
        "适合 10–100 人快速成长团队。CargoNova 开箱即用，让您专注业务增长而非系统运维。",
    },
    {
      icon: Globe,
      title: "需要本地化支持的全球化团队",
      body:
        "适合全球化扩张、需要多语言多币种与本地化响应服务的企业。CargoNova 提供中英双语支持与驻场实施。",
    },
  ],

  tableTitle: "CargoNova 与 CargoWise：能力对比",
  tableNote:
    "以下对比基于 CargoNova 的产品设计及截至 2026 年公开可获取的 CargoWise 信息，关键细节请向各厂商核实。",
  tableHeaders: ["对比维度", "CargoWise", "CargoNova"],
  table: [
    {
      icon: DollarSign,
      capability: "价格机制",
      subtitle: "Pricing",
      cargowise: "按笔计费与按用户绑定的混合模式，中小企业难以预测成本。",
      cargonova: "透明订阅 + 用量计费，月度成本更可预期。",
      badge: "更适合中小团队",
    },
    {
      icon: Rocket,
      capability: "上线难度",
      subtitle: "Deployment",
      cargowise: "通常需第三方顾问，实施周期 30–90 天。",
      cargonova: "云原生架构 + 标准迁移模板，多数客户数周内上线。",
      badge: "快速上线",
    },
    {
      icon: Sparkles,
      capability: "UI / UX 体验",
      subtitle: "User Interface",
      cargowise: "桌面式界面，菜单层级深，新员工培训成本高。",
      cargonova: "围绕 AI Copilot 设计的现代 Web 驾驶舱，新员工数天即可上手。",
      badge: "更直观",
    },
    {
      icon: Layers,
      capability: "生态连接",
      subtitle: "Integration",
      cargowise: "成熟集成较多，但与新兴电商或现代 ERP 对接可能需额外工作。",
      cargonova: "开放 RESTful API，原生兼容电商、WMS、报关及轨迹追踪系统。",
      badge: "开放互联",
    },
    {
      icon: LifeBuoy,
      capability: "客户支持",
      subtitle: "Support",
      cargowise: "以全球工单支持为主，亚太地区本地化覆盖因地区而异。",
      cargonova: "专属项目群 7×24 响应，中英双语支持，提供驻场实施。",
      badge: "全天候专属",
    },
    {
      icon: Globe,
      capability: "全球化能力",
      subtitle: "Global Coverage",
      cargowise: "覆盖非常广泛，支持全球主要国家和港口的合规、报关与多法区财税规则。",
      cargonova: "覆盖主流国际航线以及中欧美核心合规要求，部分小众国家的本地报关支持仍在持续补齐。",
      badge: "核心覆盖",
      neutral: true,
    },
  ],

  benefitsTitle: "为什么团队选择 CargoNova",
  benefits: [
    {
      metric: "数周",
      title: "更快上线",
      body:
        "得益于云原生架构与标准迁移模板，货代从既有系统迁移到 CargoNova 的周期通常以周而非季度计。",
    },
    {
      metric: "2×",
      title: "运营效率倍增",
      body:
        "AI Agent 自动化处理报价、提单、对账与轨迹追踪等日常工作，让 10 人运营团队承接过去需要更多人手才能完成的业务量。",
    },
    {
      metric: "100%",
      title: "数字化客户体验",
      body:
        "客户门户支持自助查询轨迹、下载账单、在线下单，显著提升客户留存并降低运营负担。",
    },
  ],

  faqTitle: "常见问题",
  faqs: [
    {
      q: "CargoNova 是 CargoWise 的替代方案吗？",
      a: "是的。CargoNova 是面向现代货代、跨境电商物流以及中型供应链企业的新一代 AI Native 国际货代平台，以更轻量、更智能、更具性价比的方式提供核心国际物流数字化能力。",
    },
    {
      q: "从其他平台迁移到 CargoNova 麻烦吗？需要多久？",
      a: "我们的实施团队提供全流程协助，拥有成熟的数据迁移工具，可导入客户资料、历史账单及未完结订单。多数中小团队可在数周内完成平滑迁移，业务保持连续。",
    },
    {
      q: "CargoNova 真的比 CargoWise 更具性价比吗？",
      a: "对多数中小型与中型货代来说是的。CargoNova 采用透明的订阅 + 用量计费模型，客户切换后通常在 IT 总成本上有明显下降，具体幅度取决于团队规模、模块和集成范围。",
    },
    {
      q: "什么情况下 CargoNova 可能不适合我？",
      a: "我们非常坦诚：如果您是拥有数万人规模、在全球上百个国家都有实体分公司、且严重依赖偏远国家特种本地报关系统的超大型跨国物流集团，CargoWise 目前仍是稳妥的选择。如果您是追求效率、快速扩张、主营中欧美或跨境电商主流航线的现代团队，CargoNova 会是更合适的效率工具。",
    },
  ],

  ctaTitle: "准备好升级物流运营了吗？",
  ctaBody:
    "加入正在使用 CargoNova 的现代物流团队，预约一次现场演示，看 AI Agent 如何贯穿报价、订舱、单证、关务与结算全流程。",
  ctaButton: "预约演示",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "返回 FAQ",
  backTo: "/zh",
  disclaimer:
    "CargoWise® 是 WiseTech Global 的注册商标。CargoNova 与 WiseTech Global 之间不存在任何关联、授权或赞助关系。文中所提及的产品名称、标识与品牌均为其各自所有者的财产，仅出于识别与对比目的使用。对比内容基于 CargoNova 的产品设计以及截至 2026 年公开可获取的信息，涉及评估的关键细节建议向各厂商直接核实。",
};

const contentTh = {
  eyebrow: "ทางเลือกแทน CargoWise",
  title: "CargoNova vs CargoWise:\nอันไหนเหมาะกับธุรกิจขนส่งของคุณ?",
  summary:
    "CargoNova ช่วยทีมโลจิสติกส์ที่เติบโตเร็วให้ดำเนินงานระดับโลกแบบมองเห็นภาพ real-time — สร้างมาเพื่อ workflow การขนส่งสมัยใหม่",
  intro:
    "CargoWise มีฟีเจอร์ครบครัน แต่สถาปัตยกรรมที่หนัก ระยะเวลา implement ที่ยาว และหน้าจอแบบ desktop อาจชะลอทีมที่ต้องการความคล่องตัว CargoNova คือแพลตฟอร์ม cloud-native และ AI-native สำหรับการขนส่งระหว่างประเทศยุคใหม่",

  audienceTitle: "CargoNova เหมาะกับใคร?",
  audience: [
    {
      icon: ShoppingCart,
      title: "โลจิสติกส์อีคอมเมิร์ซข้ามพรมแดน",
      body:
        "สำหรับแบรนด์ DTC ผู้ขาย Amazon และผู้ค้าข้ามพรมแดน CargoNova เชื่อมต่อกับแพลตฟอร์มอีคอมเมิร์ซและ WMS ต่างประเทศได้โดยตรง ทำให้ workflow เบาและฉลาดขึ้น",
    },
    {
      icon: Users,
      title: "ฟอร์วาร์ดเดอร์ SMEs",
      body:
        "สำหรับทีม 10–100 คนที่เติบโตเร็ว CargoNova พร้อมใช้งานทันที ให้คุณโฟกัสที่การเติบโตของธุรกิจ ไม่ใช่การดูแลระบบ",
    },
    {
      icon: Globe,
      title: "ทีมโลกที่ต้องการการสนับสนุนในท้องถิ่น",
      body:
        "สำหรับบริษัทที่ขยายไปทั่วโลกและต้องการ multi-language multi-currency และการสนับสนุนในท้องถิ่นที่ตอบสนองเร็ว CargoNova ให้บริการสนับสนุนภาษาอังกฤษ/จีนและ on-site implementation",
    },
  ],

  tableTitle: "CargoNova vs CargoWise: เปรียบเทียบความสามารถ",
  tableNote:
    "การเปรียบเทียบด้านล่างอิงตามการออกแบบของ CargoNova และข้อมูลสาธารณะของ CargoWise ณ ปี 2026 กรุณายืนยันรายละเอียดสำคัญกับแต่ละ vendor โดยตรง",
  tableHeaders: ["มิติการเปรียบเทียบ", "CargoWise", "CargoNova"],
  table: [
    {
      icon: DollarSign,
      capability: "ราคา",
      subtitle: "รูปแบบเชิงพาณิชย์",
      cargowise: "โมเดลผสมระหว่าง transaction-based และ per-user licensing ทำให้ SMEs คาดการณ์ต้นทุนยาก",
      cargonova: "Subscription โปร่งใส + usage-based pricing ช่วยให้ต้นทุนรายเดือนคาดเดาได้",
      badge: "เหมาะกับ SME",
    },
    {
      icon: Rocket,
      capability: "การติดตั้งใช้งาน",
      subtitle: "ระยะเวลาเริ่มใช้จริง",
      cargowise: "มักต้องใช้ที่ปรึกษา โดยทั่วไปใช้เวลา 30–90 วัน",
      cargonova: "สถาปัตยกรรม cloud-native + แม่แบบ migration มาตรฐาน ลูกค้าส่วนใหญ่ go-live ได้ภายในสัปดาห์",
      badge: "ติดตั้งรวดเร็ว",
    },
    {
      icon: Sparkles,
      capability: "ส่วนติดต่อผู้ใช้",
      subtitle: "ประสบการณ์ UI / UX",
      cargowise: "หน้าจอ desktop ที่มีฟีเจอร์มากและเมนูลึก ต้องอบรมเฉพาะทาง",
      cargonova: "Web cockpit สมัยใหม่ที่ออกแบบรอบ AI copilots ผู้ใช้ใหม่ใช้งานได้ภายในวัน",
      badge: "ใช้งานง่าย",
    },
    {
      icon: Layers,
      capability: "การเชื่อมต่อระบบ",
      subtitle: "การเชื่อมต่อกับระบบนิเวศ",
      cargowise: "Integration ที่มีมายาวนาน แต่การเชื่อมต่อกับ e-commerce หรือ ERP สมัยใหม่อาจต้องทำงานเพิ่ม",
      cargonova: "RESTful APIs เปิดกว้าง พร้อม connector สำหรับ e-commerce WMS ศุลกากร และผู้ให้บริการ tracking",
      badge: "เชื่อมต่อเปิดกว้าง",
    },
    {
      icon: LifeBuoy,
      capability: "การสนับสนุนลูกค้า",
      subtitle: "บริการหลังการขาย",
      cargowise: "Support ผ่าน ticket ทั่วโลก การครอบคลุมในภูมิภาคเอเชียแปซิฟิกขึ้นอยู่กับพื้นที่",
      cargonova: "ช่องทาง dedicated 24/7 สนับสนุนภาษาอังกฤษ/จีน และมี on-site implementation",
      badge: "ดูแลเฉพาะ 24/7",
    },
    {
      icon: Globe,
      capability: "ความครอบคลุมทั่วโลก",
      subtitle: "เครือข่ายระดับโลก",
      cargowise: "ครอบคลุมกว้างมาก — รองรับศุลกากร กฎระเบียบ และภาษีในประเทศและท่าเรือสำคัญทั่วโลก",
      cargonova: "ครอบคลุมเส้นทาง international หลักและ compliance ของจีน / EU / US ได้ดี ขณะที่ coverage ศุลกากรเฉพาะประเทศ niche กำลังขยายต่อเนื่อง",
      badge: "ครอบคลุมหลัก",
      neutral: true,
    },
  ],

  benefitsTitle: "ทำไมทีมถึงเปลี่ยนมาใช้ CargoNova",
  benefits: [
    {
      metric: "สัปดาห์",
      title: "Go-live เร็วขึ้น",
      body:
        "สถาปัตยกรรม cloud-native และแม่แบบ migration ช่วยให้ go-live ได้ในสัปดาห์ ไม่ใช่ไตรมาส",
    },
    {
      metric: "2×",
      title: "ประสิทธิภาพการดำเนินงาน",
      body:
        "AI agents ทำงานประจำแบบอัตโนมัติ — quoting B/L reconciliation tracking — ทีม 10 คนทำงานที่เคยต้องใช้คนสองเท่า",
    },
    {
      metric: "100%",
      title: "ประสบการณ์ดิจิทัลสำหรับลูกค้า",
      body:
        "Client portal ให้ลูกค้าตรวจสอบ shipment ดาวน์โหลด invoice และสั่งงานออนไลน์ได้ เพิ่ม retention และลดภาระงาน",
    },
  ],

  faqTitle: "คำถามที่พบบ่อย",
  faqs: [
    {
      q: "CargoNova เป็นทางเลือกแทน CargoWise หรือไม่?",
      a: "ใช่ CargoNova เป็นแพลตฟอร์มการขนส่งสมัยใหม่ที่ขับเคลื่อนด้วย AI สำหรับฟอร์วาร์ดเดอร์ยุคใหม่ ผู้ให้บริการโลจิสติกส์อีคอมเมิร์ซ และทีม supply chain ระดับกลาง — ให้ความสามารถหลักด้านโลจิสติกส์ระหว่างประเทศในแพ็กเกจที่เบากว่า ฉลาดกว่า และคุ้มค่ากว่า",
    },
    {
      q: "การย้ายมาใช้ CargoNova ยุ่งยากหรือไม่? ใช้เวลานานแค่ไหน?",
      a: "ทีม implement ของเราช่วยเหลือตลอดกระบวนการ พร้อมเครื่องมือ migration ที่สามารถนำเข้าข้อมูลลูกค้า ประวัติ invoice และ shipment ที่ค้างอยู่ ทีม SMEs ส่วนใหญ่ย้ายเสร็จภายในสัปดาห์ โดยธุรกิจดำเนินต่อเนื่อง",
    },
    {
      q: "CargoNova คุ้มค่ากว่า CargoWise จริงหรือไม่?",
      a: "สำหรับฟอร์วาร์ดเดอร์ SMEs และระดับกลางส่วนใหญ่ ใช่ CargoNova ใช้รูปแบบ subscription โปร่งใส + usage-based ลูกค้าที่ย้ายมามักรายงานต้นทุน IT ที่ลดลงอย่างมีนัยสำคัญ โดยประมาณการขึ้นอยู่กับขนาดทีม โมดูล และ integration",
    },
    {
      q: "เมื่อไหร่ CargoNova อาจไม่เหมาะ?",
      a: "เราขอพูดตรงๆ: หากคุณเป็นฟอร์วาร์ดเดอร์ข้ามชาติขนาดใหญ่มาก พนักงานหมื่นคน มีสำนักงานในหลายประเทศ และพึ่งพาระบบศุลกากรเฉพาะทางในพื้นที่ห่างไกล CargoWise ยังเป็นตัวเลือกที่ปลอดภัย หากคุณเป็นทีมที่เติบโตเร็ว โฟกัสจีน EU สหรัฐฯ หรือเส้นทางอีคอมเมิร์ซข้ามพรมแดนหลัก CargoNova เป็นเครื่องมือที่มีประสิทธิภาพสูง",
    },
  ],

  ctaTitle: "พร้อมปรับโฉมการดำเนินงานโลจิสติกส์ของคุณหรือยัง?",
  ctaBody:
    "จองการสาธิตสดเพื่อดู AI agents จัดการ quoting booking documentation customs และ settlement ใน web cockpit สมัยใหม่",
  ctaButton: "จองการสาธิต",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "กลับไปที่ FAQ",
  backTo: "/th",
  disclaimer:
    "CargoWise® เป็นเครื่องหมายการค้าจดทะเบียนของ WiseTech Global CargoNova ไม่มีความเกี่ยวข้อง ได้รับการรับรอง หรือได้รับการสนับสนุนจาก WiseTech Global ชื่อผลิตภัณฑ์ โลโก้ และแบรนด์ต่างๆ เป็นทรัพย์สินของเจ้าของที่เกี่ยวข้อง และใช้ที่นี่เพื่อการระบุและเปรียบเทียบเท่านั้น ข้อมูลเปรียบเทียบสะท้อนการออกแบบของ CargoNova และข้อมูลสาธารณะ ณ ปี 2026 กรุณายืนยันรายละเอียดสำคัญสำหรับการประเมินโดยตรงกับแต่ละ vendor",
};

const CompareCargowise = () => {
  const { language } = useLanguage();
  const isZh = language === "zh";
  const isTh = language === "th";
  const c = isZh ? contentZh : isTh ? contentTh : contentEn;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://cargonova.ai/" },
      { "@type": "ListItem", position: 2, name: "Compare", item: "https://cargonova.ai/compare" },
      {
        "@type": "ListItem",
        position: 3,
        name: "CargoNova vs CargoWise",
        item: CANONICAL,
      },
    ],
  };

  const pageTitle = isZh
    ? "CargoWise 替代方案 | CargoNova 与 CargoWise 对比选型"
    : isTh
    ? "ทางเลือกแทน CargoWise | CargoNova vs CargoWise"
    : "CargoWise Alternative: CargoNova vs CargoWise Comparison";

  const pageDescription = isZh
    ? "寻找 CargoWise 替代方案？对比 CargoNova 与 CargoWise 的价格、实施、体验、集成与支持，看国际货代 AI 系统与传统货代软件的差异。"
    : isTh
    ? "กำลังมองหาทางเลือกแทน CargoWise? เปรียบเทียบ CargoNova และ CargoWise ทั้งราคา การ implement ประสบการณ์ใช้งาน การเชื่อมต่อ และการซัพพอร์ต"
    : "Looking for a CargoWise alternative? Compare CargoNova and CargoWise on pricing, implementation, UX, integrations and support — AI-native freight forwarding software vs legacy TMS.";

  const pageCanonical = isZh ? CANONICAL_ZH : isTh ? CANONICAL_TH : CANONICAL;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={pageTitle}
        description={pageDescription}
        canonical={CANONICAL}
        canonicalZh={CANONICAL_ZH}
        canonicalTh={CANONICAL_TH}
        jsonLd={[articleSchema, faqSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-tr from-secondary/10 via-background to-accent/10">
        <div className="absolute -top-20 -right-20 w-[36rem] h-[36rem] bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[32rem] h-[32rem] bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        {/* Hero + Summary */}
        <section className="relative container mx-auto px-4 max-w-5xl pt-6 md:pt-10 pb-5 md:pb-8">
          <div className="relative">
            <span className="inline-block py-1 px-2.5 rounded-full bg-secondary/10 text-secondary text-[11px] font-semibold tracking-wider uppercase mb-4 ring-1 ring-secondary/20">
              {c.eyebrow}
            </span>
            <h1
              className={`text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-3 ${
                isZh ? "leading-[1.5159375]" : "leading-[1.25]"
              } bg-gradient-to-r from-foreground via-secondary to-foreground bg-clip-text text-transparent whitespace-pre-line`}
            >
              {c.title}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-3">
              {c.summary}
            </p>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              {c.intro}
            </p>
          </div>
        </section>

        {/* Audience */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.audienceTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.audience.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.title}
                  className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                  <div className="flex items-center gap-3 mb-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <h3 className="text-base font-semibold text-foreground leading-snug">
                      {a.title}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {a.body}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Comparison table */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-1">
            {c.tableTitle}
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-4xl leading-relaxed">
            {c.tableNote}
          </p>
          <div className="overflow-x-auto rounded-2xl ring-1 ring-border/60 bg-card/70 backdrop-blur">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary/10 text-foreground">
                  {c.tableHeaders.map((h, i) => (
                    <th
                      key={h}
                      className={`text-left font-semibold px-4 py-3 border-b border-border/50 ${
                        i === 2 ? "text-secondary" : ""
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.table.map((row, r) => {
                  const Icon = row.icon;
                  return (
                    <tr key={r} className="odd:bg-background/50">
                      <td className="px-4 py-3 border-b border-border/40 align-top font-medium text-foreground">
                        <span className="inline-flex items-start gap-2">
                          {Icon && (
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary mt-0.5">
                              <Icon className="h-3.5 w-3.5" />
                            </span>
                          )}
                          <span className="flex flex-col">
                            <span>{row.capability}</span>
                            <span className="text-[11px] font-normal text-muted-foreground">
                              {row.subtitle}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3 border-b border-border/40 align-top text-muted-foreground">
                        {row.cargowise}
                      </td>
                      <td className="px-4 py-3 border-b border-border/40 align-top text-foreground">
                        {row.badge && (
                          <span
                            className={`inline-block text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-md mb-1.5 ring-1 ${
                              row.neutral
                                ? "bg-muted text-muted-foreground ring-border/60"
                                : "bg-secondary/10 text-secondary ring-secondary/20"
                            }`}
                          >
                            {row.badge}
                          </span>
                        )}
                        <p className="leading-relaxed">{row.cargonova}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Benefits / Why switch */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.benefitsTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.benefits.map((b) => (
              <div
                key={b.title}
                className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                <div className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent mb-2 leading-none">
                  {b.metric}
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2 leading-snug">
                  {b.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.faqTitle}
          </h2>
          <div className="flex flex-col gap-3">
            {c.faqs.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl bg-card/80 backdrop-blur ring-1 ring-border/50 p-5 open:ring-secondary/50"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="text-base font-semibold text-foreground">
                    {f.q}
                  </span>
                  <ChevronDown className="h-4 w-4 text-secondary transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-muted-foreground">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative container mx-auto px-4 max-w-3xl py-8">
          <div className="rounded-3xl bg-gradient-to-br from-secondary/15 via-card to-accent/10 ring-1 ring-secondary/30 p-6 md:p-8 shadow-[0_20px_60px_-24px_hsl(var(--secondary)/0.35)]">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-2">
              {c.ctaTitle}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-5">
              {c.ctaBody}
            </p>
            <a
              href={c.ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
            >
              {c.ctaButton}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="mt-6 text-sm">
            <Link
              to={c.backTo}
              className="text-secondary hover:underline inline-flex items-center gap-1"
            >
              ← {c.backLabel}
            </Link>
          </div>

          <p className="mt-8 text-xs text-muted-foreground leading-relaxed">
            {c.disclaimer}
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CompareCargowise;
